import { Typography as MuiTypography } from "@material-ui/core";
import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";
import { z } from "zod";
import useQueryParams from "../../hooks/useQueryParams";
import FiltersForm from "../../components/FiltersForm";
import InflowScatterChartContainer from "../../components/charts/inflowScatterChart/InflowScatterChartContainer";
import { getWaterYearRange } from "../fieldDepthProfile/FieldDepthProfile";
import useDropdown from "../../hooks/useDropdown";
import React, { useMemo } from "react";
import { usePostHeight } from "../../hooks/usePostHeight";

const Section = styled.section`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
`;
const Typography = styled(MuiTypography)(spacing);

const { start_date, end_date } = getWaterYearRange();

const PARAMETER_GROUPS = [
  {
    label: "Nitrogen Concentrations",
    value: [29, 45], // total and dissolved
  },
  {
    label: "Phosphorus Concentrations",
    value: [28, 47],
  },
  {
    label: "Total Suspended Solids (TSS)",
    value: [30],
  },
];

const DEFAULTS = {
  parameterGroup: [28, 47], // Default to phosphorus for the phosphorus-concentrations-2025 route
  start_date,
  end_date,
  locationIds: ["68", "83"],
};

const schema = z.object({
  parameterGroup: z.preprocess(
    (val) => (typeof val === "string" ? val.split(",").map(Number) : val),
    z.array(z.number()).default(DEFAULTS.parameterGroup)
  ),
  start_date: z.string().default(DEFAULTS.start_date),
  end_date: z.string().default(DEFAULTS.end_date),
  locationIds: z.preprocess(
    (v) => (typeof v === "string" ? v.split(",") : v),
    z.array(z.string()).default(DEFAULTS.locationIds)
  ),
  controls: z.preprocess(
    (val) => val === "true" || val === true,
    z.boolean().default(false)
  ),
  height: z.preprocess(
    (val) => (typeof val === "string" ? parseInt(val, 10) : val),
    z.number().default(400)
  ),
});

export default function CcrInflowConcentrationsEmbed() {
  usePostHeight();
  const { params, setParams } = useQueryParams(schema);

  const locOptions = useDropdown(
    "results-simple/download-dropdown-locs-active"
  );

  const getParameterGroupLabel = (parameterGroup) => {
    const group = PARAMETER_GROUPS.find(
      (g) => JSON.stringify(g.value) === JSON.stringify(parameterGroup)
    );
    return group ? group.label : "Water Quality Parameters";
  };

  const FILTERS = useMemo(
    () => [
      {
        type: "select",
        param: "parameterGroup",
        label: "Parameter Group",
        default: params.parameterGroup.map(String),
        options: PARAMETER_GROUPS.map((group) => ({
          label: group.label,
          value: group.value.map(String).join(","),
        })),
      },
      {
        type: "date",
        param: "start_date",
        label: "Start Date",
        default: params.start_date,
      },
      {
        type: "date",
        param: "end_date",
        label: "End Date",
        default: params.end_date,
      },
      {
        type: "auto",
        multi: true,
        param: "locationIds",
        label: "Locations",
        default: params.locationIds.map(String),
        options: locOptions,
      },
    ],
    [locOptions, params]
  );

  return (
    <>
      <main>
        {params.controls && (
          <Section>
            <Typography variant="h5" component="h2" gutterBottom>
              Filters
            </Typography>
            <FiltersForm config={FILTERS} onChange={setParams} />
          </Section>
        )}

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            {getParameterGroupLabel(params.parameterGroup)}
          </Typography>
          <InflowScatterChartContainer
            parameterGroup={params.parameterGroup}
            startDate={params.start_date}
            endDate={params.end_date}
            locationIds={params.locationIds}
            height={params.height}
          />
        </Section>
      </main>
    </>
  );
}
